import { Group, Layout, Page, Tree } from "@/comn/components";
import { useNavigate, useParams } from "react-router-dom";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { TFormSchema, useFetch, useForm, useModal, usePage, useResource, useToast } from "@/comn/hooks";
import { useTranslation } from "react-i18next";
import { APIS, BASE, URLS, SF_COMN_VRFN_CTRA_INFO } from "@/tra/tancis/ptli/comnmdle/doc/services/PtliDocMgmtService";
import { useEffect } from "react";

/*
 * Common Verification Criteria Modification
 * !== 공통검증기준 수정 ==!
 */
export const PTLI0413003S = () => {
    useResource({
        defaultSchema: [
            { area: "tb" },
            { area: "col" },
            { area: "lbl" },
            { area: "comnCdMstr" },
            { area: "indvCdTb" },
            { area: "indvCdCol" },
        ],
    });

    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const navigate = useNavigate();
    const { param } = useParams();
    const modal = useModal();
    const toast = useToast();
    const { t } = useTranslation();

    const form = {
        comnVrfnCtraInfo: useForm({
            defaultSchema: SF_COMN_VRFN_CTRA_INFO,
        }),
    };

    const fetch = {
        getComnVrfnCtraInfo: useFetch({
            api: () => APIS.getComnVrfnCtraInfo(param),
            onSuccess: (data: any) => {
                // 데이터가 없을 경우
                if (!data.comnVrfnCtraInfo.content) {
                    modal.openModal({ content: "msg.com.00034" }); // No data found. !== 해당 데이터가 없습니다. ==!

                    navigate(`${URLS.ptli0413001q}?menuId=${menuId}`);
                    return;
                }
                console.log(data);
                form.comnVrfnCtraInfo.setValues(data.comnVrfnCtraInfo.content);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00018" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),
        updateComnVrfnCtraInfo: useFetch({
            api: (info: any) => APIS.updateComnVrfnCtraInfo(info),
            enabled: false,
            onSuccess: (data) => {
                console.log(data);
                if (data?.msgList?.content.length > 0) {
                    // 일반 메시지 모달
                    modal.openModal({
                        type: "messages",
                        data: data?.msgList.content,
                    });
                } else {
                    modal.openModal({ content: "msg.com.00059" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
        deleteComnVrfnCtraInfo: useFetch({
            api: (info: any) => APIS.deleteComnVrfnCtraInfo(info),
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00060" }); // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                navigate(`${URLS.ptli0413001q}?menuId=${menuId}`);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00023" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),
    };

    const handler = {
        onListBtnClick: () => {
            navigate(`${URLS.ptli0413001q}?menuId=${menuId}`);
        },
        onUpdateBtnClick: form.comnVrfnCtraInfo.handleSubmit(
            () => {
                modal.openModal({
                    content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.updateComnVrfnCtraInfo.fetch(form.comnVrfnCtraInfo.getValues()),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => fetch.deleteComnVrfnCtraInfo.fetch(form.comnVrfnCtraInfo.getValues()),
            });
        },
    };

    useEffect(() => {
        fetch.getComnVrfnCtraInfo.fetch();
    }, []);

    const itmLenValdnYn = form.comnVrfnCtraInfo.watch("itmLenValdnYn") == "Y";
    const dtVldtyValdnYn = form.comnVrfnCtraInfo.watch("dtVldtyValdnYn") == "Y";
    const numDgtValdnYn = form.comnVrfnCtraInfo.watch("numDgtValdnYn") == "Y";
    const comnCdValdnYn = form.comnVrfnCtraInfo.watch("comnCdValdnYn") == "Y";
    const indvCdValdnYn = form.comnVrfnCtraInfo.watch("indvCdValdnYn") == "Y";

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={t("T_COMN_INFO")} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.dclaDocCd} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.docNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.trgtTbNm} readOnly={true} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.trgtColNm} readOnly={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.valdnItmLblId} />
                        </Group.Row>
                    </Group.Section>
                    <Group.Title title={t("L_VALDN_RULE")} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.itmSrtngOrd} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.erkyOrd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.rqrdInpValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.itmLenValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.itmMinLenDgt} required={itmLenValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.itmLenDgt} required={itmLenValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.dtVldtyValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.dtVldtyTpCn} required={dtVldtyValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.numVldtyValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.numDgtValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.totDgt} required={numDgtValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.dcmlUndrDgt} required={numDgtValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.amtQtyValdnYn} />
                        </Group.Row>
                    </Group.Section>
                    <Group.Title title={t("L_CD_VALDN")} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.comnCdValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.comnCd} required={comnCdValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.indvCdValdnYn} />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.indvCdUprColNm} required={indvCdValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.comnVrfnCtraInfo.schema.indvCdTbNm}
                                callback={(data) => {
                                    form.comnVrfnCtraInfo.schema.indvCdColNm.popupParams.tbId = data.code;
                                }}
                                onChange={(value) => {
                                    form.comnVrfnCtraInfo.schema.indvCdColNm.popupParams.tbId = value;
                                }}
                                required={indvCdValdnYn}
                            />
                            <Group.Control {...form.comnVrfnCtraInfo.schema.indvCdColNm} required={indvCdValdnYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.comnVrfnCtraInfo.schema.indvCdAddQuryCn} required={indvCdValdnYn} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                    <Layout.Button role={"delete"} onClick={handler.onDeleteBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
